export const ACCEPTED_MIME = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/gif": [".gif"],
  "image/webp": [".webp"],
  "image/svg+xml": [".svg"],
  "video/mp4": [".mp4"],
  "video/quicktime": [".mov"],
  "video/webm": [".webm"],
  "video/x-msvideo": [".avi"],
  "text/vcard": [".vcf"],
} as const;

export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 25 * 1024 * 1024;

export type RejectionReason = "type" | "image-too-large" | "video-too-large";

export type FilterResult = {
  accepted: File[];
  rejected: { file: File; reason: RejectionReason }[];
};

const isImageMime = (type: string) => type.startsWith("image/");
const isVideoMime = (type: string) => type.startsWith("video/");

const isAcceptedMime = (type: string): boolean =>
  Object.prototype.hasOwnProperty.call(ACCEPTED_MIME, type);

export function filterValidFiles(files: File[]): FilterResult {
  const accepted: File[] = [];
  const rejected: FilterResult["rejected"] = [];

  for (const file of files) {
    if (!isAcceptedMime(file.type)) {
      rejected.push({ file, reason: "type" });
      continue;
    }
    if (isImageMime(file.type) && file.size > MAX_IMAGE_BYTES) {
      rejected.push({ file, reason: "image-too-large" });
      continue;
    }
    if (isVideoMime(file.type) && file.size > MAX_VIDEO_BYTES) {
      rejected.push({ file, reason: "video-too-large" });
      continue;
    }
    accepted.push(file);
  }

  return { accepted, rejected };
}

const REASON_TEXT: Record<RejectionReason, string> = {
  type: "unsupported file type",
  "image-too-large": "image too large",
  "video-too-large": "video too large",
};

export function buildRejectionMessage(
  rejected: FilterResult["rejected"],
): string {
  if (rejected.length === 0) return "";
  const reasons = Array.from(
    new Set(rejected.map((r) => REASON_TEXT[r.reason])),
  );
  const fileWord = rejected.length === 1 ? "file" : "files";
  return `${rejected.length} ${fileWord} skipped: ${reasons.join(", ")}.`;
}
